import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Bot, Globe, ImageIcon } from "lucide-react";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import AdminSEORefresh from "./AdminSEORefresh";
import AdminSEOIntegrations from "./AdminSEOIntegrations";
import AdminBranding from "./AdminBranding";

const PROVIDERS = [
  { id: "emergent", label: "Emergent Universal" },
  { id: "openai", label: "OpenAI" },
  { id: "anthropic", label: "Anthropic" },
  { id: "gemini", label: "Gemini" },
];

const MODELS = {
  emergent: ["gpt-5-mini", "gpt-5", "gpt-4o-mini", "claude-sonnet-4-5-20250929", "gemini-2.5-flash"],
  openai: ["gpt-5-mini", "gpt-5", "gpt-4o-mini", "gpt-4.1-mini", "gpt-4o"],
  anthropic: ["claude-sonnet-4-5-20250929", "claude-haiku-4-5-20251001"],
  gemini: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-2.5-flash-lite"],
};

export default function AdminSettings() {
  const [fx, setFx] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [ai, setAi] = useState({ provider: "emergent", model: "gpt-5-mini" });
  const [imgProvider, setImgProvider] = useState(null);
  const [savingImg, setSavingImg] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadFx = async () => {
    const { data } = await api.get("/fx/rates");
    setFx(data);
  };
  useEffect(() => {
    loadFx();
    api.get("/admin/ai-provider").then(r => setAi({ provider: r.data.provider, model: r.data.model })).catch(()=>{});
    api.get("/ai/images/provider").then(r => setImgProvider(r.data)).catch(()=>{});
  }, []);

  const refreshFx = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/fx/refresh");
      toast.success(`Refreshed ${data.currencies} rates`);
      await loadFx();
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const saveAi = async () => {
    setSaving(true);
    try { await api.post("/admin/ai-provider", ai); toast.success("AI provider saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Settings</h2>
        <p className="text-slate-400 text-sm">Platform-wide configuration</p>
      </div>

      {/* FX */}
      <div className="card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider"><Globe size={14}/> Foreign Exchange</div>
            <h3 className="text-xl font-bold text-white mt-1" style={{fontFamily:'Outfit'}}>Live FX Rates</h3>
            {fx && (
              <div className="text-sm text-slate-400 mt-1">
                {Object.keys(fx.rates).length} currencies · Source: <span className="text-slate-200">{fx.source}</span> ·
                {" "}Last updated: <span className="text-slate-200">{fx.fetched_at ? new Date(fx.fetched_at).toLocaleString() : "—"}</span>
              </div>
            )}
          </div>
          <button onClick={refreshFx} disabled={refreshing} className="btn-primary !py-2 !px-4 text-sm" data-testid="fx-refresh-btn">
            {refreshing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refresh Rates
          </button>
        </div>
        {fx && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-64 overflow-y-auto text-xs">
            {Object.entries(fx.rates).slice(0, 60).map(([c, r]) => (
              <div key={c} className="bg-slate-800/60 border border-slate-700 rounded px-2 py-1.5">
                <span className="text-slate-400">1 INR = </span>
                <span className="text-white font-semibold">{Number(r).toFixed(4)} {c}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI provider */}
      <div className="card p-6">
        <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider"><Bot size={14}/> AI Provider</div>
        <h3 className="text-xl font-bold text-white mt-1" style={{fontFamily:'Outfit'}}>Active AI Engine</h3>
        <p className="text-slate-400 text-sm mt-1">All AI features (Website Builder, App Builder, Content/SEO/Logo/Chat) use this provider.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="label">Provider</label>
            <select value={ai.provider} onChange={e=>setAi({provider:e.target.value, model: MODELS[e.target.value][0]})} className="input" data-testid="ai-provider-select">
              {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Model</label>
            <select value={ai.model} onChange={e=>setAi({...ai, model:e.target.value})} className="input" data-testid="ai-model-select">
              {MODELS[ai.provider].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <button onClick={saveAi} disabled={saving} className="btn-primary mt-5 inline-flex items-center gap-2" data-testid="ai-save">
          {saving ? <Loader2 size={14} className="animate-spin"/> : null} Save Configuration
        </button>
      </div>

      {/* AI Image provider */}
      <div className="card p-6">
        <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider"><ImageIcon size={14}/> AI Image Provider</div>
        <h3 className="text-xl font-bold text-white mt-1" style={{fontFamily:'Outfit'}}>Default Image Engine</h3>
        <p className="text-slate-400 text-sm mt-1">Default provider for customer image generation. Customers can override per-request.</p>
        {imgProvider && (
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className="label">Provider</label>
              <select
                value={imgProvider.provider}
                onChange={e => setImgProvider({...imgProvider, provider: e.target.value})}
                className="input" data-testid="img-provider-select"
              >
                {imgProvider.options.map(p => <option key={p} value={p}>{imgProvider.labels[p]}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={async () => {
                  setSavingImg(true);
                  try { await api.post("/ai/images/provider", { provider: imgProvider.provider }); toast.success("Image provider saved"); }
                  catch { toast.error("Save failed"); }
                  finally { setSavingImg(false); }
                }}
                disabled={savingImg}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="img-provider-save"
              >
                {savingImg ? <Loader2 size={14} className="animate-spin"/> : null} Save
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SEO refresh */}
      <AdminSEORefresh/>

      {/* SEO Suite integrations (paid keyword API) */}
      <AdminSEOIntegrations/>

      {/* Branding — logo + footer */}
      <AdminBranding/>

      {/* Webhooks */}
      <div className="card p-6">
        <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider">Payments</div>
        <h3 className="text-xl font-bold text-white mt-1" style={{fontFamily:'Outfit'}}>Razorpay Webhooks</h3>
        <p className="text-slate-400 text-sm mt-2">Register this URL in Razorpay Dashboard → Webhooks:</p>
        <code className="block mt-3 p-3 rounded-lg bg-slate-900 border border-slate-700 text-cyan-300 text-xs select-all" data-testid="webhook-url">
          {typeof window !== 'undefined' ? window.location.origin : 'https://optivize.digital'}/api/webhooks/razorpay
        </code>
        <p className="text-slate-400 text-xs mt-3">Subscribe to events: <span className="text-slate-200">payment.captured, payment.failed, order.paid, refund.processed, qr_code.credited, subscription.charged, subscription.activated, subscription.cancelled, subscription.halted</span>. Set `RAZORPAY_WEBHOOK_SECRET` in backend env and rotate periodically.</p>
      </div>

      {/* Change password */}
      <ChangePasswordCard/>
    </div>
  );
}
